import React, { useMemo, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import {
	ExperienceFailure,
	TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
} from '@confluence/experience-tracker';

import type { ContentMode } from '../presenceTypes';
import { getActivityToDisplay, getCurrentUserFromMap } from '../presenceUtils';
import { usePresenceStoreState } from '../presence-store';
import { useJumpToTelepointer } from '../useJumpToTelepointer';

import { usePresenceProvider } from './usePresenceProvider';
import { ContentHeaderPresenceContainer } from './ContentHeaderPresenceContainer';

const i18n = defineMessages({
	anonymousUser: {
		id: 'team-presence.avatar-group.anonymous-user',
		defaultMessage: 'Anonymous Person',
		description:
			'The default user name provided for anonymous users when displayed in the avatar group within the real-time presence experience',
	},
});

export type PresenceProps = {
	contentId: string;
	contentMode: ContentMode;
	contentType: string;
	analyticSource?: string;
};

export const Presence: FC<PresenceProps> = ({
	contentId,
	contentMode,
	contentType,
	analyticSource,
}) => {
	const [{ isEditMode }] = useLivePageMode();
	const { formatMessage } = useIntl();
	const activity = contentMode === 'edit' || isEditMode ? 'editor' : 'viewer';

	const { sessionId, fetchMore, networkError, getTelepointerBySessionId } = usePresenceProvider(
		contentId,
		contentType,
		contentMode,
		activity,
	);

	const { participants, totalCount } = usePresenceStoreState();
	const { createJumpToTelepointerHandler } = useJumpToTelepointer({
		contentMode,
		getTelepointerBySessionId,
	});

	/*
	 * Memoize the participants to ensure that we only show unique viewers and editors
	 */
	const memoizedParticipants = useMemo(() => {
		const participantValues = [...participants.values()];
		const currentUser = getCurrentUserFromMap(participants, sessionId);

		return participantValues.map((participant) => {
			return {
				...participant,
				name:
					participant.userId === 'unidentified'
						? formatMessage(i18n.anonymousUser)
						: participant.name,
				activity:
					participant.isCurrentUser && currentUser
						? getActivityToDisplay(participant.sessions, contentMode, sessionId)
						: getActivityToDisplay(participant.sessions, contentMode),
				onClick: createJumpToTelepointerHandler(participant),
			};
		});
	}, [participants, sessionId, contentMode, formatMessage, createJumpToTelepointerHandler]);

	if (networkError) {
		return (
			<ExperienceFailure name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} error={networkError} />
		);
	}

	return (
		<ContentHeaderPresenceContainer
			contentId={contentId}
			contentMode={contentMode}
			contentType={contentType}
			analyticSource={analyticSource}
			currentUserPresenceSessionId={sessionId}
			onLoadMoreClick={fetchMore}
			participants={memoizedParticipants}
			totalParticipants={totalCount}
		/>
	);
};
